/**
 * evidenceSourceVocabulary — the one shared vocabulary for `analysis.evidence_sources`, the field
 * that records which inputs an analysis actually used.
 *
 * Two surfaces print this field to the same technician: the handoff briefing (`HandoffBriefing`,
 * section "ראיות ומקורות") and the run page (`AgentRunDetailPage`, "מקורות ראיות"). Both render
 * `map[token] || token`, so a key a map lacks is not an unlabelled corner case — it is the
 * producer's own English snake_case identifier on a Hebrew screen. Held per surface, the map is
 * therefore the difference between a label and a raw token on one page and not the other, which is
 * what happened: both carried only the five tokens they were first written against, so both printed
 * `connector_evidence` and `customer_environment` raw. One map here, free of JSX and of any data
 * access, is what makes that impossible — the same reason `statusVocabulary` exists.
 *
 * The key set is the producer's closed set, `direct_llm_analyst._VALID_EVIDENCE_SOURCES`, which
 * `MockAnalysisAdapter` in `helpdesk_analyst.py` also appends to (it writes the field directly and
 * is not run through that filter). `evidenceSourceVocabulary.test.jsx` reads both producer modules
 * and fails if this map drifts from either.
 */
export const EVIDENCE_SOURCE_LABEL = {
  ticket_title: "כותרת הקריאה",
  ticket_description: "תיאור הקריאה",
  customer_context: "הקשר לקוח",
  knowledge_base: "מאגר ידע",
  similar_tickets: "קריאות דומות",
  // Built on the product's own word for this entity: ActivityLogPage labels `customer_profile`
  // "סביבת IT" and names its events "יצר/ה סביבת IT ללקוח". The possessor is spelled out because a
  // chip in an evidence list has no surrounding sentence to say whose environment it was.
  customer_environment: "סביבת IT של הלקוח",
  // Provider-neutral on purpose — HELPDESK-E2E-001 named the category that way so a later
  // read-only evidence source reuses it rather than becoming a new one, and neither surface may
  // name a vendor the evidence did not necessarily come from.
  connector_evidence: "ראיות מהמחבר",
};

/**
 * The conversation trace's own field, which is NOT the one above and only shares a word with it.
 *
 * `technical_trace.evidence_source` is a single token naming where a reply's evidence came from.
 * Its producer is `conversation_service._build_technical_trace` — not the analysis adapters — and
 * its value set is `agent_memory` / `agent_run` / `connector_evidence` / `employee_registry`, of
 * which only the third is also an `analysis.evidence_sources` token. Two surfaces print it, both as
 * the bare value under a Hebrew label: `ConversationPanel`'s collapsed trace box ("מקור מידע", which
 * reaches three mounts — the conversation page, the ticket's `ai-chat` tab and the employee
 * workspace) and `HandoffCenterPage`'s evidence drawer ("מקור ראיה"). Neither mapped it, so all
 * four arrived at a technician as the producer's own English identifier.
 *
 * It gets its own map rather than being folded into `EVIDENCE_SOURCE_LABEL`: that map's parity test
 * holds it *equal* to the analysis parser's closed set, and adding three tokens no analysis can emit
 * would either break that equality or weaken it to a subset check. The one token the two fields do
 * share is defined once, above, and referenced here, so the two surfaces cannot describe the same
 * source in two different words.
 */
export const TRACE_EVIDENCE_SOURCE_LABEL = {
  // The producer sets this arm when the reply carried cited sources at all (`reply.evidence_sources`
  // is non-empty), and the row directly beneath it prints those very sources as `evidence_summary`.
  // Deliberately NOT "זיכרון לקוח": that is this repo's label for the `agent_memory` *entity* the
  // system log writes, and this token asserts no stored memory row existed — on the pilot default
  // none did, because the fallback cites the ticket's own fields.
  agent_memory: "מקורות שהעובד ציטט",
  // The trace reports on an AgentRun; this is the product's own word for one, and the same box
  // already uses it for the run this trace links to.
  agent_run: "ריצת AI",
  connector_evidence: EVIDENCE_SOURCE_LABEL.connector_evidence,
  // "מרשם" is this product's word for a registry (מרשם המחברים / מרשם הכלים / מרשם המודלים); the
  // manager consulted this one to pick the employee.
  employee_registry: "מרשם העובדים",
};

/**
 * The producer's "no evidence" sentinel. It is not a source, and no surface labels it: two places
 * compare against this exact string in order to drop the row — `ConversationPanel`'s trace box, and
 * `handoffCenterModel.evidenceFrom`, which nulls the field so that `HandoffCenterPage`'s drawer has
 * no source row left to draw. A change to the producer's default would pass both guards and print
 * the new sentinel on both surfaces, so the string is pinned on both sides by the tests below.
 */
export const TRACE_EVIDENCE_SOURCE_NONE = "none";

/** An unrecognised token is shown as itself: an unmapped arm must stay visible rather than blank. */
export const traceEvidenceSourceLabel = (token) =>
  TRACE_EVIDENCE_SOURCE_LABEL[token] ?? token;
